import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {ID} from '@wandb/weave/common/util/id';
import {Button} from '@wandb/weave/components/Button';
import _ from 'lodash';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {type DropdownProps, Modal} from 'semantic-ui-react';

import {
  useMoveViewMutation,
  useUserProjectsQuery,
} from '../../generated/graphql';
import * as Analytics from '../../services/analytics';
import {useApolloClient, useSelector} from '../../state/hooks';
import * as ReportTypes from '../../state/reports/types';
import {type ReportViewRef} from '../../state/reports/types';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewApi from '../../state/views/api';
import * as ViewNormalize from '../../state/views/normalize';
import {type ReportConfig} from '../../state/views/report/types';
import * as ViewSelectors from '../../state/views/selectors';
import {type SaveableView} from '../../state/views/types';
import {MAX_REPORT_DISPLAY_NAME_LENGTH} from '../../util/constants';
import {type ReportAuthor} from '../../util/section';
import * as Urls from '../../util/urls';
import {getProjectsFromUserProjectsQuery} from '../CreateReportModal';
import {useProjectOptions} from './hooks';

type CloneReportModalProps = {
  moveReport?: boolean;
  viewRef: ReportViewRef;
  isPublicProject: boolean;
  onClose(): void;
};

const CloneReportModalComp = ({
  moveReport,
  viewRef,
  isPublicProject,
  onClose,
}: CloneReportModalProps) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const client = useApolloClient();
  const history = useHistory();
  const viewer = useViewer();
  const views = useSelector(ViewSelectors.getViews);

  const view = useSelector(state => state.views.views[viewRef.id]);
  if (view.project == null) {
    throw new Error('attempting to clone a report without a project');
  }
  const {id: projectID} = view.project;
  if (projectID == null) {
    throw new Error('invalid state');
  }
  const viewPartRef = view.partRef;
  if (viewPartRef == null) {
    throw new Error('View in redux missing part ref');
  }

  const userProjectsQuery = useUserProjectsQuery({
    variables: {
      userName: viewer?.username ?? '',
      pattern: searchFilter,
      first: 20,
    },
    skip: viewer == null,
  });
  const projectOptions = useProjectOptions(userProjectsQuery, view.project);

  const [selectedProjectID, setSelectedProjectID] = useState(
    view.project.id || ''
  );
  const selectedProject = useMemo(() => {
    if (userProjectsQuery.loading) {
      return view.project;
    }
    const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
    return projects.find(p => p.id === selectedProjectID) || view.project;
  }, [selectedProjectID, userProjectsQuery, view.project]);

  const onChangeProject = useCallback(
    (e: React.SyntheticEvent, data: DropdownProps) => {
      setSelectedProjectID(data.value as string);
    },
    []
  );

  useEffect(() => {
    const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
    if (projects.length === 0) {
      return;
    }
    // if a project on the list is already selected, don't change it against the user's will
    let newProjectId = projects.find(p => p.id === selectedProjectID)?.id;
    // otherwise, select current project
    if (newProjectId == null) {
      newProjectId = projectID;
    }
    // if current project is not on the list, default to first project
    if (newProjectId == null) {
      newProjectId = projects[0]?.id;
    }
    setSelectedProjectID(newProjectId);
    // eslint-disable-next-line
  }, [userProjectsQuery, projectID]);

  const onCloneReport = useCallback(async () => {
    Analytics.reportCloneClicked({
      reportType:
        viewRef.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE
          ? 'report draft'
          : 'published report',
      location: 'report page',
      isPublicProject,
    });

    if (selectedProject == null) {
      return;
    }

    const spec = ViewNormalize.denormalize(views.parts, viewPartRef, {
      includeRefs: false,
    }) as ReportConfig;

    const cloner = _.pick(viewer, 'name', 'username');
    spec.authors = [cloner as ReportAuthor];

    setIsLoading(true);
    const project = _.pick(
      selectedProject,
      'entityName',
      'name'
    ) as NonNullable<SaveableView['project']>;

    const newDraft = await ViewApi.save(client, {
      type: 'runs/draft',
      name: ID(12),
      displayName: `Copy of ${view.user.username}'s ${view.displayName}`.slice(
        0,
        MAX_REPORT_DISPLAY_NAME_LENGTH
      ),
      description: view.description,
      spec,
      project,
      previewUrl: view.previewUrl,
      coverUrl: view.coverUrl,
    });

    setIsLoading(false);
    setIsModalOpen(false);

    history.push(
      Urls.reportEdit(
        {
          entityName: project.entityName,
          projectName: project.name!,
          reportID: newDraft.id as string,
          reportName: newDraft.displayName,
        },
        `?draftId=${newDraft.id}`
      )
    );
  }, [
    viewRef.type,
    isPublicProject,
    selectedProject,
    setIsLoading,
    views.parts,
    viewPartRef,
    viewer,
    client,
    view.user.username,
    view.displayName,
    view.description,
    view.previewUrl,
    view.coverUrl,
    history,
  ]);

  const [moveView] = useMoveViewMutation();
  const onMoveReport = useCallback(async () => {
    if (view.id == null || selectedProject == null) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await moveView({
        variables: {
          id: view.id,
          entityName: selectedProject.entityName,
          projectName: selectedProject.name,
        },
      });
      if (!res?.data?.moveView?.success) {
        throw new Error(`failed to move report ${view.id}`);
      }
    } catch (err) {
      alert(`Error moving report: ${err}`);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsModalOpen(false);

    history.push(
      Urls.reportView({
        entityName: selectedProject.entityName,
        projectName: selectedProject.name,
        reportID: view.id,
        reportName: view.displayName,
      })
    );
  }, [
    view.id,
    view.displayName,
    history,
    setIsLoading,
    moveView,
    selectedProject,
  ]);

  return (
    <Modal className="clone-report-modal" open={isModalOpen} onClose={onClose}>
      <Modal.Header>{moveReport ? 'Move' : 'Clone'} Report</Modal.Header>
      <Modal.Content>
        <p className="clone-report-modal__text">
          Pick a destination for your {moveReport ? 'moved' : 'cloned'} report.
        </p>
        <ModifiedDropdown
          selection
          search
          className="clone-report-modal__select"
          onSearchChange={(e, data) => {
            setSearchFilter(data.searchQuery);
          }}
          options={projectOptions}
          value={selectedProjectID}
          onChange={onChangeProject}
          hideText
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-test="clone-report-button"
          disabled={isLoading}
          onClick={moveReport ? onMoveReport : onCloneReport}>
          {`${moveReport ? 'Move' : 'Clone'} report`}
        </Button>
        <Button variant="ghost" disabled={isLoading} onClick={onClose}>
          Back
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const CloneReportModal = memo(CloneReportModalComp);
